/* __next_internal_client_entry_do_not_use__ FlashSaleImageGridV2 auto */ import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { useSettingDealsHub } from "@faire/retailer-visitor-shared/settings/useSettingDealsHub";
import { debounceIfPossible } from "@faire/web--source/common/globals/debounceIfPossible";
import { getWindow } from "@faire/web--source/common/globals/getWindow";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { optimizeProductImageUrl } from "@faire/web--source/common/transform-images";
import { Opacity } from "@faire/web--source/slate/Color";
import { Row } from "@faire/web--source/slate/Layout";
import { Typography } from "@faire/web--source/slate/Typography";
import { TransparencyOverlay } from "@faire/web--source/ui/TransparencyOverlay";
import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
var IMAGE_GRID_HEIGHT = 200;
var TextOverlay = function(props) {
    var numAdditionalItems = props.numAdditionalItems, showOverlay = props.showOverlay;
    var isFlashDealsRevamp = useSettingDealsHub();
    if ((numAdditionalItems !== null && numAdditionalItems !== void 0 ? numAdditionalItems : 0) <= 0 || !showOverlay) {
        return null;
    }
    return /*#__PURE__*/ _jsx(Overlay, {
        children: /*#__PURE__*/ _jsx(OverlayTextContainer, {
            children: /*#__PURE__*/ _jsx(Typography, {
                variant: "sectionHeaderSansMedium",
                color: Core.text.inverse,
                children: "".concat(isFlashDealsRevamp ? "+" : "+ ").concat((numAdditionalItems !== null && numAdditionalItems !== void 0 ? numAdditionalItems : 0) + 1)
            })
        })
    });
};
var getImageOptions = function(numImages, isFirst, gridWidth) {
    var option = {
        resizeMethod: "fill",
        ignoreResizeTag: true
    };
    if (numImages === 1) {
        return {
            resizeMethod: "pad",
            background: Core.surface.subdued,
            height: IMAGE_GRID_HEIGHT,
            width: gridWidth
        };
    }
    if (numImages === 2) {
        return _object_spread_props(_object_spread({}, option), {
            height: IMAGE_GRID_HEIGHT,
            width: gridWidth / 2
        });
    }
    if (isFirst) {
        return _object_spread_props(_object_spread({}, option), {
            height: IMAGE_GRID_HEIGHT,
            width: gridWidth * (2 / 3)
        });
    }
    return _object_spread_props(_object_spread({}, option), {
        height: IMAGE_GRID_HEIGHT / 2,
        width: gridWidth / 3
    });
};
export var FlashSaleImageGridV2 = function(props) {
    var images = props.images;
    var isFlashDealsRevamp = useSettingDealsHub();
    var ref = useRef(null);
    var _useState = _sliced_to_array(useState(0), 2), gridWidth = _useState[0], setGridWidth = _useState[1];
    useEffect(function() {
        var _getWindow;
        var getWidth = function() {
            var _ref_current;
            var _ref_current_offsetWidth;
            return (_ref_current_offsetWidth = (_ref_current = ref.current) === null || _ref_current === void 0 ? void 0 : _ref_current.offsetWidth) !== null && _ref_current_offsetWidth !== void 0 ? _ref_current_offsetWidth : 0;
        };
        var handleResize = debounceIfPossible(function() {
            if (getWidth() !== gridWidth) {
                setGridWidth(getWidth());
            }
        }, 200);
        if (ref.current) {
            setGridWidth(getWidth());
        }
        (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : _getWindow.addEventListener("resize", handleResize);
        return function() {
            var _getWindow;
            (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : _getWindow.removeEventListener("resize", handleResize);
        };
    }, [
        ref,
        gridWidth
    ]);
    var numImages = Math.min(images.length, 3);
    var numAdditionalItems = Math.max(0, images.length - 3);
    var isSSR = useIsSSR();
    return /*#__PURE__*/ _jsx(Grid, {
        $numImages: numImages,
        ref: ref,
        children: gridWidth > 1 ? images.slice(0, numImages).map(function(image, index) {
            var isFirst = index === 0 && numImages === 3;
            var imageOptions = getImageOptions(numImages, isFirst, gridWidth);
            var _imageOptions_height, _imageOptions_width;
            return /*#__PURE__*/ _jsxs(ImageWrapper, {
                $isFirst: isFirst,
                $maxHeight: (_imageOptions_height = imageOptions.height) !== null && _imageOptions_height !== void 0 ? _imageOptions_height : IMAGE_GRID_HEIGHT,
                $maxWidth: (_imageOptions_width = imageOptions.width) !== null && _imageOptions_width !== void 0 ? _imageOptions_width : gridWidth,
                children: [
                    /*#__PURE__*/ _jsx(TextOverlay, {
                        numAdditionalItems: numAdditionalItems,
                        showOverlay: numAdditionalItems > 0 && index === 2
                    }),
                    isFlashDealsRevamp ? /*#__PURE__*/ _jsx(ImageOverlay, {
                        showOverlay: index !== 2 && numAdditionalItems > 0
                    }) : null,
                    /*#__PURE__*/ _jsx("img", {
                        src: optimizeProductImageUrl(image, isSSR, 200, imageOptions),
                        loading: undefined
                    })
                ]
            }, image.token);
        }) : null
    });
};
var ImageOverlay = function(param) {
    var showOverlay = param.showOverlay;
    if (!showOverlay) {
        return null;
    }
    return /*#__PURE__*/ _jsx(TransparencyOverlay, {
        style: {
            zIndex: 2,
            display: "block",
            background: Core.surface.enabled,
            opacity: 0.02
        }
    });
};
var Overlay = styled(TransparencyOverlay).withConfig({
    componentId: "sc-4622b608-0"
})([
    "z-index:2;display:block;background:",
    ";"
], Core.text.enabled + Opacity.O_35);
var OverlayTextContainer = styled(Row).attrs({
    justify: "center",
    align: "center"
}).withConfig({
    componentId: "sc-4622b608-1"
})([
    "height:100%;width:100%;"
]);
var getGridSize = function(numImages) {
    if (numImages === 1) {
        return {
            columns: "1 fr",
            rows: "1 fr"
        };
    }
    if (numImages === 2) {
        return {
            columns: "1fr 1fr",
            rows: "1 fr"
        };
    }
    return {
        columns: "2fr 1fr",
        rows: "1fr 1fr"
    };
};
var Grid = styled.div.withConfig({
    componentId: "sc-4622b608-2"
})([
    "display:grid;grid-gap:2px;flex-shrink:0;height:",
    "px;",
    ""
], IMAGE_GRID_HEIGHT, function(param) {
    var $numImages = param.$numImages;
    return css([
        "grid-template-columns:",
        ";grid-template-rows:",
        ";"
    ], getGridSize($numImages).columns, getGridSize($numImages).rows);
});
var ImageWrapper = styled.div.withConfig({
    componentId: "sc-4622b608-3"
})([
    "width:100%;height:100%;position:relative;",
    " img{display:block;width:100%;height:100%;object-fit:contain;max-width:",
    "px;max-height:",
    "px;}"
], function(props) {
    return props.$isFirst ? "grid-row: 1 / span 2;" : "";
}, function(props) {
    return props.$maxWidth;
}, function(props) {
    return props.$maxHeight;
});
